import type { Conversation } from './types';

// Agent-facing conversation model (feedback #7/#9): reframe the confusing
// open/pending tabs around WHO is handling a conversation right now. Derived
// from existing fields (aiHandled + assigneeUid + status) — no schema change.
//
//   New         → AI is managing it; no agent needed yet
//   Open        → escalated, waiting for an agent to pick up (AI off, unassigned)
//   In progress → an agent owns it (assigned)
//   Waiting …   → parked on someone else (the customer, or another team)
//   Resolved    → closed
export type InboxView =
  | 'ai'
  | 'waiting'
  | 'progress'
  | 'wait_customer'
  | 'wait_internal'
  | 'flagged'
  | 'resolved'
  | 'all'
  // Simple-mode GROUPS over the buckets above (see GROUP_BUCKETS). They exist so
  // the default bar can offer three tabs instead of eight; advanced keeps a tab
  // per bucket, unchanged.
  | 'needs_you'
  | 'waiting_any';

export const VIEWS: Array<{ key: InboxView; label: string; hint: string }> = [
  { key: 'ai', label: 'New (AI)', hint: 'AI is managing — no agent needed yet' },
  { key: 'waiting', label: 'Open (Pending)', hint: 'Escalated — waiting for an agent to pick up' },
  { key: 'progress', label: 'In Progress (Human)', hint: 'An agent is handling this' },
  { key: 'wait_customer', label: 'Waiting (Customer)', hint: 'Parked — the ball is with the customer, nothing for us to do yet' },
  { key: 'wait_internal', label: 'Waiting (Internal)', hint: 'Parked — blocked on another team internally' },
  { key: 'flagged', label: 'AI Flagged', hint: 'AI answers that need a look — agent-flagged or errored. Mark reviewed to clear.' },
  { key: 'resolved', label: 'Resolved', hint: 'Closed conversations' },
  { key: 'all', label: 'All', hint: 'Everything' },
];

// The eight tabs above wrapped onto four lines before an agent had read a single
// message, so the DEFAULT (non-advanced) bar shows three plus an overflow menu.
// Nothing is removed: advanced still renders VIEWS exactly as it always has, and
// the grouped tabs are derived from the same buckets (GROUP_BUCKETS) the eight
// are, so the two bars can never disagree about where a conversation lives.
export const SIMPLE_VIEWS: Array<{ key: InboxView; label: string; hint: string }> = [
  {
    key: 'needs_you',
    label: 'Needs you',
    hint: 'Escalated or already yours — the only queue that needs an agent right now',
  },
  {
    key: 'waiting_any',
    label: 'Waiting',
    hint: 'Parked on the customer or on another team — nothing to do here yet',
  },
  // Same 'resolved' tab, plainer name: it is the end of the simple flow, not a
  // status the agent has to reason about.
  { key: 'resolved', label: 'Done', hint: 'Closed conversations' },
];

/** The remaining queues, behind the simple bar's overflow menu. Taken FROM VIEWS
 *  (order included) so both bars label and describe a queue identically. */
export const SIMPLE_MORE_VIEWS = VIEWS.filter(
  (v) => v.key === 'ai' || v.key === 'flagged' || v.key === 'all',
);

export type ChatBucket =
  | 'ai'
  | 'waiting'
  | 'progress'
  | 'wait_customer'
  | 'wait_internal'
  | 'resolved'
  | 'snoozed';

/** Which bucket a conversation falls into. Snoozed is its own bucket so it
 *  stays out of the active queues until it wakes (visible under All). */
export function bucketOf(
  c: Pick<Conversation, 'status' | 'assigneeUid' | 'aiHandled' | 'waitReason'>,
): ChatBucket {
  if (c.status === 'resolved') return 'resolved';
  // Parked chats are snoozed ones carrying a waitReason — matched BEFORE the
  // plain snoozed line, else they'd fall into the no-tab 'snoozed' bucket and
  // only ever surface under All. The status check keeps a stale waitReason
  // (anything that reopens a chat without clearing it) from stranding the
  // conversation in a parked tab instead of the live queues.
  if (c.status === 'snoozed' && c.waitReason === 'customer') return 'wait_customer';
  if (c.status === 'snoozed' && c.waitReason === 'internal') return 'wait_internal';
  if (c.status === 'snoozed') return 'snoozed';
  if (c.assigneeUid) return 'progress';
  if (c.aiHandled === false) return 'waiting';
  return 'ai';
}

export type ViewGroup = 'needs_you' | 'waiting_any';

/** Which buckets each grouped (simple-mode) tab collapses. THE single source for
 *  the grouping: the tab's filter, its badge count, the deep-link target and the
 *  fallback for a remembered advanced-only tab are all derived from this table,
 *  so a group can never drift from the bucket tabs it stands in for. Bucket names
 *  double as the advanced view keys (Extract<> pins that), which is what lets a
 *  group expand back into a tab. */
export const GROUP_BUCKETS: Record<ViewGroup, Array<Extract<ChatBucket, InboxView>>> = {
  needs_you: ['waiting', 'progress'],
  waiting_any: ['wait_customer', 'wait_internal'],
};

const GROUP_KEYS = Object.keys(GROUP_BUCKETS) as ViewGroup[];

export function isViewGroup(view: InboxView): view is ViewGroup {
  return (GROUP_KEYS as string[]).includes(view);
}

/** Every key either bar can hold — validate a remembered/saved view against this
 *  before trusting it, or a stale key wedges the bar on nothing selectable. */
const ALL_VIEW_KEYS: string[] = [...VIEWS.map((v) => v.key), ...GROUP_KEYS];

export function isInboxView(v: string): v is InboxView {
  return ALL_VIEW_KEYS.includes(v);
}

/**
 * The tab that is actually ON SCREEN for `view` in the bar currently rendered.
 *
 * `grouped` = the simple bar is up (non-advanced, classic layout): a bucket tab
 * collapses into the group holding it. Ungrouped — advanced, and the 'new'
 * layout's rail, which has room to list every queue — a group expands back to
 * its first member. Keys both bars offer (ai/flagged/resolved/all) pass through.
 */
export function viewForMode(view: InboxView, grouped: boolean): InboxView {
  if (!grouped) return isViewGroup(view) ? GROUP_BUCKETS[view][0] : view;
  for (const g of GROUP_KEYS) {
    if (GROUP_BUCKETS[g].some((m) => m === view)) return g;
  }
  return view;
}

/** Label of the tab `view` renders as in the current bar. Only 'resolved'
 *  differs (Resolved / Done), but the empty state names the tab, so it has to
 *  ask rather than assume. */
export function labelForView(view: InboxView, grouped: boolean): string {
  for (const bar of grouped ? [SIMPLE_VIEWS, VIEWS] : [VIEWS, SIMPLE_VIEWS]) {
    const hit = bar.find((v) => v.key === view);
    if (hit) return hit.label;
  }
  return '';
}

/** The tab a conversation should open under when deep-linked. Mirrors
 *  bucketOf/matchesView so it can never drift from the list filter, and resolves
 *  through viewForMode so the target is a tab the CURRENT bar actually shows —
 *  in the simple bar a 'progress' chat lands on "Needs you", not on a tab that
 *  isn't there. Plain 'snoozed' has no dedicated tab, so it surfaces under
 *  'all'; the two parked buckets DO have one (their own, or the group), so they
 *  are deliberately not remapped. */
export function viewForChat(
  c: Pick<Conversation, 'status' | 'assigneeUid' | 'aiHandled' | 'waitReason'>,
  grouped: boolean,
): InboxView {
  const b = bucketOf(c);
  if (b === 'snoozed') return 'all';
  return viewForMode(b, grouped); // 'ai' | 'waiting' | … | 'resolved' — all real tabs
}

/** Does a conversation belong in the given view tab? */
export function matchesView(c: Conversation, view: InboxView): boolean {
  if (view === 'all') return true;
  if (view === 'resolved') return c.status === 'resolved';
  // Orthogonal to the handling buckets: the AI-Flagged review worklist. Keyed on
  // the dedicated aiFlagged marker (F11) so a flag survives newer turns and can
  // be explicitly cleared — the old `lastAiDecision.feedback.verdict` check lost
  // the flag the moment any newer AI reply overwrote lastAiDecision.
  if (view === 'flagged') return !!c.aiFlagged;
  const b = bucketOf(c);
  // A grouped tab holds exactly the buckets listed for it — the rules aren't
  // restated here, they're read off GROUP_BUCKETS.
  if (isViewGroup(view)) return GROUP_BUCKETS[view].some((m) => m === b);
  return b === view;
}
